import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Repeat, X } from "lucide-react";
import { toast } from "sonner";
import { getSubscriptions } from "@/lib/api";
import { categoryColor, formatEUR, formatEUR0 } from "@/lib/format";
import { FixedExpensesSection } from "@/components/FixedExpensesSection";

const PINK = "#FF7AB6";

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
};

const Stat = ({ label, value, color, testid }) => (
  <div className="flex-1 rounded-[8px] border border-white/[0.06] bg-[#0D0D14] px-3 py-2.5" data-testid={testid}>
    <p className="caps-tag text-white/35">{label}</p>
    <p className="mono mt-1 text-[15px] font-medium tabular-nums" style={{ color }}>
      {value}
    </p>
  </div>
);

export const SubscriptionsSheet = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null); // indice abbonamento aperto

  const load = useCallback(async () => {
    setData(null);
    setExpanded(null);
    try {
      setData(await getSubscriptions());
    } catch {
      toast.error("Impossibile caricare gli abbonamenti");
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      load();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const subs = data?.subscriptions || [];

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="subscriptions-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em]" style={{ color: PINK }}>
                <Repeat size={15} />
                ABBONAMENTI <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="subscriptions-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {data === null ? (
                <div className="space-y-3" data-testid="subscriptions-loading">
                  <div className="h-16 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-14 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  ))}
                </div>
              ) : subs.length === 0 ? (
                <div
                  className="flex flex-col items-center rounded-[10px] border border-dashed border-white/[0.1] bg-[#0A0A12] px-4 py-7 text-center"
                  data-testid="subscriptions-empty"
                >
                  <span className="mono text-lg" style={{ color: PINK }}>⟳</span>
                  <p className="mono mt-3 text-[13px] font-light text-white/85">Nessun abbonamento ricorrente rilevato</p>
                  <p className="mt-1.5 max-w-[260px] text-[12px] font-light text-white/40">
                    Quando rileverò spese che si ripetono ogni mese le troverai qui.
                  </p>
                </div>
              ) : (
                <>
                  {/* riepilogo */}
                  <div className="flex gap-2">
                    <Stat label="Al mese" value={formatEUR0(data.monthly_total)} color={PINK} testid="subs-stat-monthly" />
                    <Stat label="Stima annua" value={formatEUR0(data.yearly_total)} color="#FF7A9C" testid="subs-stat-yearly" />
                    <Stat label="Servizi" value={String(data.count)} color="rgba(255,255,255,0.9)" testid="subs-stat-count" />
                  </div>

                  {/* lista */}
                  <p className="caps-tag mb-2 mt-5 text-white/50">Spese ricorrenti rilevate</p>
                  <div className="space-y-2" data-testid="subscriptions-list">
                    {subs.map((s, i) => {
                      const color = categoryColor(s.category);
                      const isOpen = expanded === i;
                      const charges = s.charges || [];
                      return (
                        <div
                          key={`${s.merchant}-${i}`}
                          className="overflow-hidden rounded-[10px] border border-white/[0.06] bg-[#0D0D14]"
                          data-testid={`subscription-row-${i}`}
                        >
                          <button
                            onClick={() => setExpanded(isOpen ? null : i)}
                            data-testid={`subscription-toggle-${i}`}
                            aria-expanded={isOpen}
                            className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-white/[0.03] active:bg-white/[0.05]"
                          >
                            <span
                              className="mono flex h-9 w-9 shrink-0 items-center justify-center rounded-[7px] border text-[12px]"
                              style={{ borderColor: `${color}55`, color, background: `${color}12` }}
                            >
                              {(s.merchant || "?").slice(0, 2).toUpperCase()}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-[13px] font-light text-white/85" data-testid="subscription-merchant">
                                  {s.merchant}
                                </p>
                                {s.is_new && (
                                  <span
                                    className="caps-tag shrink-0 rounded-[4px] px-1.5 py-0.5"
                                    style={{ color: PINK, border: `1px solid ${PINK}55` }}
                                    data-testid="subscription-new-badge"
                                  >
                                    Nuovo
                                  </span>
                                )}
                              </div>
                              <p className="mono mt-0.5 text-[11px] tracking-[0.04em] text-white/35">
                                <span className="inline-flex items-center gap-1">
                                  <span className="h-1.5 w-1.5 rounded-[2px]" style={{ backgroundColor: color }} />
                                  {s.category}
                                </span>
                                <span className="mx-1.5 text-white/20">·</span>
                                rinnovo ~ il {s.typical_day}
                                {s.platform && (
                                  <>
                                    <span className="mx-1.5 text-white/20">·</span>
                                    <span className="text-[#00FF87]/70" data-testid="subscription-platform">
                                      via {s.platform}
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <div className="text-right">
                                <p className="mono text-[14px] font-medium tabular-nums text-white/90">{formatEUR(s.amount)}</p>
                                <p className="caps-tag text-white/30">al mese</p>
                              </div>
                              <ChevronDown
                                size={14}
                                className={`text-white/30 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                              />
                            </div>
                          </button>

                          {isOpen && (
                            <div className="border-t border-white/[0.06] px-3 py-3" data-testid={`subscription-detail-${i}`}>
                              <div className="mb-2 flex items-center justify-between">
                                <span className="caps-tag text-white/45">Ultimi addebiti</span>
                                <span className="caps-tag text-white/30">
                                  {s.count} rilevati · ultimo {fmtDay(s.last_date)}
                                </span>
                              </div>
                              {charges.length === 0 ? (
                                <p className="py-1 text-center text-[11px] font-light text-white/35">
                                  Nessun dettaglio disponibile
                                </p>
                              ) : (
                                <div className="space-y-1.5">
                                  {charges.map((c) => (
                                    <div key={c.id} className="flex items-center justify-between gap-2" data-testid="subscription-charge">
                                      <span className="flex min-w-0 items-center gap-1.5">
                                        <span className="mono shrink-0 text-[10px] uppercase text-white/30">{fmtDay(c.date)}</span>
                                        <span className="truncate text-[11px] font-light text-white/60">{c.description}</span>
                                      </span>
                                      <span className="mono shrink-0 text-[11px] tabular-nums text-white/65">{formatEUR(c.amount)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <p className="mono mt-2.5 text-[10px] leading-4 tracking-[0.03em] text-white/25">
                                Stima annua di questo servizio · {formatEUR0(s.amount * 12)}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <p className="mono mt-4 px-1 text-center text-[10px] leading-4 tracking-[0.05em] text-white/25">
                    Rilevamento automatico dai movimenti ricorrenti · controlla quelli che non usi più
                  </p>
                </>
              )}

              {/* Spese fisse manuali — sempre disponibili */}
              <div className="mt-2 border-t border-white/[0.06] pt-1">
                <FixedExpensesSection active={open} />
              </div>
            </div>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
